import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { authStore } from '../../store/authStore';
import { client } from '../services/apollo';
import { gql } from '@apollo/client';
import {
  firebaseMessagingServiceWorkerUrl,
  getFirebaseConfig,
  getFirebaseVapidKey,
} from '../../config/firebase-config';
import { getBrand } from '../constants/brands';

const UPDATE_FCM_TOKEN = gql`
  mutation UpdateFcmToken($fcmToken: String) {
    updateFcmToken(fcmToken: $fcmToken)
  }
`;

/**
 * Hook to initialize Firebase Cloud Messaging and register device for push notifications
 * Only works on browsers with support for Notifications API and Service Workers
 */
export const useFcmInitialization = (): void => {
  const auth = useSnapshot(authStore);
  const isAuthenticated = auth.isAuthenticated && auth.user?.id;

  useEffect(() => {
    if (!isAuthenticated) {
      return; // Don't initialize FCM until user is authenticated
    }

    // Only initialize in browsers that support FCM
    if (
      typeof window === 'undefined' ||
      !('Notification' in window) ||
      !('serviceWorker' in navigator)
    ) {
      console.debug('[FCM] Browser does not support notifications');
      return;
    }

    // Skip if already initialized
    if (sessionStorage.getItem('fcm_initialized')) {
      console.debug('[FCM] Already initialized in this session');
      return;
    }

    const initializeFcm = async (): Promise<void> => {
      try {
        // Check if user has already granted permission
        if (Notification.permission === 'denied') {
          console.debug('[FCM] User has denied notifications');
          return;
        }

        // Request permission if not yet requested
        if (Notification.permission === 'default') {
          console.debug('[FCM] Requesting notification permission...');
          const permission = await Notification.requestPermission();

          if (permission !== 'granted') {
            console.debug('[FCM] User declined notifications');
            return;
          }
        }

        // Register the service worker with this page's Firebase config and icon in its
        // URL. Always register: a worker registered by an older build (no config in
        // the URL) is replaced, so it stops falling back to a hostname guess.
        const firebaseConfig = getFirebaseConfig();
        const notificationIcon = getBrand().assets.notificationIcon || '/logo192.png';
        let serviceWorkerRegistration: ServiceWorkerRegistration | undefined;
        let serviceWorkerReady = false;
        console.debug('[FCM] Registering service worker...');
        try {
          const registration = await navigator.serviceWorker.register(
            firebaseMessagingServiceWorkerUrl(firebaseConfig, notificationIcon),
            {
              scope: '/',
            },
          );
          console.log('[FCM] Service worker registered successfully');
          serviceWorkerRegistration = registration;
          serviceWorkerReady = true;

          // Monitor for service worker errors
          registration.addEventListener('error', event => {
            console.error('[FCM] Service worker error:', event);
          });
        } catch (error) {
          console.warn('[FCM] Failed to register service worker:', error);
          // Continue anyway - FCM might still work with foreground messages
          serviceWorkerReady = false;
        }

        // Dynamically import Firebase messaging
        let getMessaging, getToken, onMessage, initializeApp, getApps, isSupported;
        try {
          const messagingImport = await import('firebase/messaging');
          const appImport = await import('firebase/app');
          getMessaging = messagingImport.getMessaging;
          getToken = messagingImport.getToken;
          onMessage = messagingImport.onMessage;
          isSupported = messagingImport.isSupported;
          initializeApp = appImport.initializeApp;
          getApps = appImport.getApps;
        } catch (error) {
          console.error('[FCM] Failed to import Firebase modules:', error);
          return;
        }

        // Check if this browser supports Firebase Messaging
        const supported = await isSupported();
        if (!supported) {
          console.debug('[FCM] Browser does not support Firebase Messaging');
          return;
        }

        let app = getApps()[0];
        if (!app) {
          app = initializeApp(firebaseConfig);
        }

        const messaging = getMessaging(app);

        // Get FCM token
        console.debug('[FCM] Getting FCM token...');
        let fcmToken: string | undefined;
        try {
          fcmToken = await getToken(messaging, {
            vapidKey: getFirebaseVapidKey(),
            // Without this the SDK registers a second copy of the worker at its own
            // scope, which never receives the config above.
            ...(serviceWorkerRegistration ? { serviceWorkerRegistration } : {}),
          });
        } catch (error) {
          console.warn('[FCM] Failed to get FCM token:', error);
          fcmToken = undefined;
        }

        if (fcmToken) {
          console.log('[FCM] Got FCM token:', fcmToken.substring(0, 20) + '...');

          // Send token to backend
          try {
            const result = await client.mutate({
              mutation: UPDATE_FCM_TOKEN,
              variables: { fcmToken },
            });

            if (result.data?.updateFcmToken) {
              console.log('[FCM] FCM token registered with backend');
              sessionStorage.setItem('fcm_initialized', 'true');
              sessionStorage.setItem('fcm_token_timestamp', Date.now().toString());
            } else {
              console.warn('[FCM] Failed to register FCM token with backend');
            }
          } catch (error) {
            console.error('[FCM] Failed to send FCM token to backend:', error);
          }
        } else {
          console.warn(
            '[FCM] Failed to get FCM token, but FCM initialization will continue for foreground messages',
          );
          // Don't set sessionStorage so we can retry later
        }

        // Listen for foreground messages
        try {
          onMessage(messaging, payload => {
            console.log('[FCM] Received foreground message:', payload);

            // Show notification even in foreground
            if (payload.notification) {
              new Notification(payload.notification.title || '', {
                body: payload.notification.body,
                icon: notificationIcon,
                badge: '/favicon.ico',
                data: payload.data,
                tag: payload.data?.notificationId || 'notification',
              });
            }
          });
          console.log('[FCM] Foreground message listener set up');
        } catch (error) {
          console.warn('[FCM] Failed to set up foreground message listener:', error);
        }
      } catch (error) {
        console.error('[FCM] Initialization failed:', error);
      }
    };

    // Delay initialization slightly to ensure everything is ready
    const timer = setTimeout(() => {
      initializeFcm().catch(error => {
        console.error('[FCM] Initialization error:', error);
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [isAuthenticated]);
};
